import React from 'react';
import { Link } from 'react-router-dom';
import { FaFacebookF, FaYoutube, FaWhatsapp, FaInstagram, FaPhoneAlt, FaMapMarkerAlt, FaHeart } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  
  return (
    <footer className="relative bg-black text-white overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-white"></div>
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-10 right-10 w-64 h-64 rounded-full bg-gray-800 opacity-20"></div>
        <div className="absolute bottom-40 left-10 w-40 h-40 rounded-full bg-gray-800 opacity-10"></div>
      </div>
      
      <div className="container mx-auto px-6 pt-16 pb-8 relative z-10">
        {/* Logo and Mission */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-12">
          <div className="flex items-center mb-6 md:mb-0">
            <div className="h-16 w-16 rounded-full overflow-hidden border-2 border-white/30 mr-4">
              <img 
                src="/Logo.jpeg" 
                alt="Karigar Foundation Logo" 
                className="h-full w-full object-cover" 
              />
            </div>
            <div>
              <h2 className="text-2xl font-heading font-bold text-white">Karigar Foundation</h2>
              <p className="text-sm text-gray-400">मजदूरों की सेवा में</p>
            </div>
          </div>
          
          <div className="md:max-w-md text-center md:text-right">
            <p className="text-gray-400">
              {t('footer_about_desc')}
            </p>
          </div>
        </div>
        
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 py-8 border-t border-gray-800">
          {/* About Column */}
          <div className="animate-fade-in">
            <h3 className="text-xl font-heading font-bold mb-6 text-white relative inline-block">
              {t('footer_about')}
              <span className="absolute -bottom-2 left-0 w-1/2 h-0.5 bg-white"></span>
            </h3>
            <p className="text-gray-400 mb-6 leading-relaxed">
              {t('footer_about_desc')}
            </p>
            <div className="flex space-x-4">
              <a
                href="https://www.facebook.com/people/Karigar-Foundation/61563256746316/"
                target="_blank"
                rel="noreferrer"
                aria-label="Facebook"
                className="w-9 h-9 rounded-full bg-gray-800 hover:bg-white transition-all duration-300 flex items-center justify-center text-white hover:text-black"
              >
                <FaFacebookF />
              </a>
              <a
                href="https://youtube.com/@karigar_foundation123"
                target="_blank"
                rel="noreferrer"
                aria-label="YouTube"
                className="w-9 h-9 rounded-full bg-gray-800 hover:bg-white transition-all duration-300 flex items-center justify-center text-white hover:text-black"
              >
                <FaYoutube />
              </a>
              <a
                href="https://www.instagram.com/karigar_foundation/"
                target="_blank"
                rel="noreferrer"
                aria-label="Instagram"
                className="w-9 h-9 rounded-full bg-gray-800 hover:bg-white transition-all duration-300 flex items-center justify-center text-white hover:text-black"
              >
                <FaInstagram />
              </a>
              <a
                href="https://chat.whatsapp.com/H6Ers4inWaSALFHYNDHRew"
                target="_blank"
                rel="noreferrer"
                aria-label="WhatsApp Group"
                className="w-9 h-9 rounded-full bg-gray-800 hover:bg-white transition-all duration-300 flex items-center justify-center text-white hover:text-black"
              >
                <FaWhatsapp />
              </a>
            </div>
          </div>
          
          {/* Quick Links Column */}
          <div className="animate-fade-in" style={{ animationDelay: '200ms' }}>
            <h3 className="text-xl font-heading font-bold mb-6 text-white relative inline-block">
              {t('footer_links')}
              <span className="absolute -bottom-2 left-0 w-1/2 h-0.5 bg-white"></span>
            </h3>
            <ul className="space-y-3">
              {[
                { to: '/', label: t('home') },
                { to: '/about', label: t('about') },
                { to: '/projects', label: t('projects') },
                { to: '/impact', label: t('impact') },
                { to: '/events', label: t('events.title', 'Events') },
                { to: '/contact', label: t('contact') },
                { to: '/donate', label: t('donate') }
              ].map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.to} 
                    className="text-gray-400 hover:text-white transition-colors duration-300 flex items-center"
                  >
                    <svg className="w-3 h-3 mr-2 text-white" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Latest News Column */}
          <div className="animate-fade-in" style={{ animationDelay: '400ms' }}>
            <h3 className="text-xl font-heading font-bold mb-6 text-white relative inline-block">
              {t('footer_news')}
              <span className="absolute -bottom-2 left-0 w-1/2 h-0.5 bg-white"></span>
            </h3>
            <ul className="space-y-4">
              {[
                { date: '03 Sep 2025', title: t('footer_news1') },
                { date: '28 Aug 2025', title: t('footer_news2') },
                { date: '15 Aug 2025', title: t('footer_news3') },
                { date: '07 Aug 2025', title: t('footer_news4') }
              ].map((news, index) => (
                <li key={index} className="group">
                  <div className="block">
                    <p className="text-xs text-white mb-1">{news.date}</p>
                    <p className="text-gray-400 group-hover:text-white transition-colors duration-300">{news.title}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Contact Column */}
          <div className="animate-fade-in" style={{ animationDelay: '600ms' }}>
            <h3 className="text-xl font-heading font-bold mb-6 text-white relative inline-block">
              {t('footer_contact')}
              <span className="absolute -bottom-2 left-0 w-1/2 h-0.5 bg-white"></span>
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start">
                <span className="flex-shrink-0 w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center mr-3 mt-0.5">
                  <FaMapMarkerAlt className="text-white" />
                </span>
                <span className="text-gray-400">
                  Saket Nagar, Lalganj, Raebareli, Uttar Pradesh 229206
                </span>
              </li>
              <li className="flex items-start">
                <span className="flex-shrink-0 w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center mr-3 mt-0.5">
                  <FaPhoneAlt className="text-white" />
                </span>
                <span className="text-gray-400">
                  +91 8090846400 / +91 8400866400
                </span>
              </li>
              {/* <li className="flex items-start">
                <span className="flex-shrink-0 w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center mr-3 mt-0.5">
                  <FaEnvelope className="text-white" />
                </span>
                <span className="text-gray-400">
                  rohit.kumar@kaarigarfoundation.org
                </span>
              </li> */}
            </ul>
          </div>
        </div>
        
        {/* Newsletter */}
        {/* <div className="mt-8 pt-8 border-t border-gray-800">
          <div className="max-w-2xl mx-auto">
            <div className="bg-gray-900 rounded-xl p-6 md:p-8 shadow-lg">
              <div className="flex flex-col md:flex-row md:items-center">
                <div className="flex-1 mb-4 md:mb-0 md:mr-6">
                  <h4 className="text-xl font-heading font-bold text-white mb-2">Subscribe to Our Newsletter</h4>
                  <p className="text-gray-400">Stay updated with our latest news, events and initiatives</p>
                </div>
                <div className="flex-1">
                  <form className="flex">
                    <input 
                      type="email" 
                      placeholder="Your Email Address" 
                      className="flex-1 py-3 px-4 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-white bg-black border-none text-white placeholder-gray-500"
                      required
                    />
                    <button 
                      type="submit" 
                      className="bg-white hover:bg-gray-200 text-black font-medium py-3 px-6 rounded-r-lg transition-colors duration-300"
                    >
                      Subscribe
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div> */}
        
        {/* Bottom Footer */}
        <div className="mt-12 pt-6 border-t border-primary-600 text-center">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-primary-200 text-sm mb-4 md:mb-0">
              &copy; {currentYear} Karigar Foundation. {t('footer_rights')} 
              <span className="inline-flex items-center ml-1">
                Made with <FaHeart className="text-accent mx-1 animate-pulse" /> in India
              </span>
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <Link to="/contact" className="text-primary-200 hover:text-white text-sm transition-colors duration-300">
                {t('footer_privacy')}
              </Link>
              <Link to="/contact" className="text-primary-200 hover:text-white text-sm transition-colors duration-300">
                {t('footer_terms')}
              </Link>
              <Link to="/contact" className="text-primary-200 hover:text-white text-sm transition-colors duration-300">
                {t('contact')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
